import { useState } from "react";
import { newsStore, type NewsItem } from "../utils/dataStore";

export default function NewsAudit() {
  const [items, setItems] = useState<NewsItem[]>(() =>
    newsStore.getAll().filter((n) => n.status === "pending")
  );
  const [previewItem, setPreviewItem] = useState<NewsItem | null>(null);
  const [rejectModal, setRejectModal] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const handleApprove = (id: string) => {
    newsStore.update(id, { status: "published" });
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (previewItem?.id === id) setPreviewItem(null);
  };

  const handleReject = (id: string) => {
    newsStore.update(id, { status: "draft" });
    setItems((prev) => prev.filter((n) => n.id !== id));
    setRejectModal(null);
    setRejectReason("");
    if (previewItem?.id === id) setPreviewItem(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900">资讯审核</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {items.length > 0
              ? `共 ${items.length} 篇资讯待审核`
              : "暂无待审核资讯"}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-amber-50 text-amber-600 px-3 py-1.5 rounded-full">
          <i className="ri-time-line" />
          待审核
          {items.length > 0 && (
            <span className="ml-1 w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
              {items.length}
            </span>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="py-20 text-center">
          <i className="ri-checkbox-circle-line text-5xl text-green-200 block mb-3" />
          <p className="text-sm font-semibold text-gray-400">所有资讯均已审核完毕</p>
          <p className="text-xs text-gray-300 mt-1">暂无待审核的资讯，稍后再来查看</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {["标题 / 摘要", "分类", "作者", "投稿时间", "操作"].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((n) => (
                <tr key={n.id} className="hover:bg-amber-50/30 transition-colors">
                  <td className="px-5 py-4 max-w-xs">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-text-line text-amber-500 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 line-clamp-1">{n.title}</p>
                        {n.summary && (
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">{n.summary}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full whitespace-nowrap">{n.category}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-gray-400 text-xs" />
                      </div>
                      <span className="text-gray-600 text-xs whitespace-nowrap">{n.author}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-400 text-xs whitespace-nowrap">{n.publishDate}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewItem(n)}
                        className="px-3 py-1.5 bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-eye-line" />预览
                      </button>
                      <button
                        onClick={() => handleApprove(n.id)}
                        className="px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-checkbox-circle-line" />通过
                      </button>
                      <button
                        onClick={() => { setRejectModal(n.id); setRejectReason(""); }}
                        className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-close-circle-line" />退回
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && setPreviewItem(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-1 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full flex items-center gap-1">
                  <i className="ri-time-line" />待审核
                </span>
                <span className="px-2.5 py-1 bg-red-50 text-red-600 text-xs rounded-full">{previewItem.category}</span>
              </div>
              <button
                onClick={() => setPreviewItem(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3 leading-snug">{previewItem.title}</h2>
              <div className="flex items-center gap-4 text-xs text-gray-400 mb-5">
                <span className="flex items-center gap-1"><i className="ri-user-line" />{previewItem.author}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line" />{previewItem.publishDate}</span>
              </div>
              {previewItem.coverUrl && (
                <div className="rounded-xl overflow-hidden mb-4" style={{ height: 180 }}>
                  <img src={previewItem.coverUrl} alt="封面" className="w-full h-full object-cover object-top" />
                </div>
              )}
              {previewItem.summary && (
                <div className="bg-amber-50 rounded-xl px-4 py-3 mb-4">
                  <p className="text-xs font-semibold text-amber-700 mb-1">摘要</p>
                  <p className="text-sm text-amber-800 leading-relaxed">{previewItem.summary}</p>
                </div>
              )}
              {previewItem.content ? (
                <div className="text-sm text-gray-700 leading-loose whitespace-pre-wrap">{previewItem.content}</div>
              ) : (
                <div className="py-8 text-center">
                  <i className="ri-file-text-line text-3xl text-gray-200 block mb-2" />
                  <p className="text-sm text-gray-400">暂无正文内容</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={() => { setRejectModal(previewItem.id); setPreviewItem(null); setRejectReason(""); }}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 rounded-xl text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                退回修改
              </button>
              <button
                onClick={() => handleApprove(previewItem.id)}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-checkbox-circle-line" />审核通过 · 立即发布
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-close-circle-line text-red-500 text-lg" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">退回资讯</h3>
                <p className="text-xs text-gray-400 mt-0.5">资讯将退回草稿状态，作者需重新修改提交</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">退回原因（选填）</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="如：内容不符合协会规定、需补充数据来源等..."
                rows={3}
                maxLength={200}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-red-400"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleReject(rejectModal)}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                确认退回
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
